import FormItem from '@/shared/FormCreator/FormItem'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { useForm } from 'react-hook-form'

import Button from '@/shared/Button'
import { useEffect, useState } from 'react'

import ChangePasswordDialog from '@/components/ChangePasswordDialog'
import { DATE_FORMAT_CN } from '@/libs/core/constants'
import get from 'lodash/get'
import merge from 'lodash/merge'
import moment from 'moment'
import Circle from '@mui/icons-material/Circle'
import useIsReportOnlyRole from '@/hooks/useIsReportOnlyRole'

const mapStateToProps = ({ account, loading }) => ({
  data: account.userData,
  planDetails: account.planDetails,
  isLoading: loading['account/getSelfData'],
})

const inputConfig = {
  inputBaseProps: {
    sx: { justifyContent: 'flex-start' },
    labelSx: { minWidth: 80 },
    childrenSx: { width: 250 },
  },
}

const PersonalDetailsPage = ({ dispatch, data, planDetails }) => {
  const [changePassword, setChangePassword] = useState(false)
  const { t } = useTranslation()
  const isReportOnlyRole = useIsReportOnlyRole()

  const {
    courseCount = 0,
    emailCount = 0,
    planName = 0,
    reportCount = 0,
    preEmailCount = 0,
  } = planDetails || {}

  const form = useForm({
    mode: 'onBlur',
  })

  const { control } = form

  useEffect(() => {
    dispatch({ type: 'account/getSelfData', payload: {} })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const { setValue } = form

    if (data) {
      const {
        email,
        accountKey,
        accountValue,
        roleName,
        custodian,
        plan,
        planId,
        planStartTime,
        planEndTime,
        emailCount: emailCounts,
        preEmailCount: preEmailCounts,
        manualEmailCount,
        manualPreEmailCount,
      } = data || {}
      const username = get(email?.split('@'), '[0]')

      setValue('email', email)
      setValue('username', username)
      setValue('type', roleName)
      setValue('key', accountKey || '-')
      setValue('value', accountValue || '-')
      setValue('manager', custodian || '-')
      setValue('tier', plan || '-')
      setValue(
        'validity',
        planStartTime && planEndTime
          ? `${moment(planStartTime).format(DATE_FORMAT_CN)} 至 ${moment(planEndTime).format(
              DATE_FORMAT_CN,
            )}`
          : '-',
      )
      setValue('emailCount', emailCounts || '0')
      setValue('preEmailCount', preEmailCounts || '0')
      setValue('manualEmailCount', manualEmailCount || '0')
      setValue('manualPreEmailCount', manualPreEmailCount || '0')

      dispatch({ type: 'account/getPlanDetails', payload: { planId } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Container>
      <Stack direction={'row'} spacing={4}>
        <Stack>
          {!isReportOnlyRole && (
            <>
              <Typography mb={1}>{t('general.purchase_plan')}</Typography>
              <Card>
                <CardContent>
                  <Stack direction={'row'} spacing={4}>
                    <Stack spacing={2}>
                      <FormItem
                        defaultValue=""
                        label={t('general.send_quantity')}
                        name="emailCount"
                        control={control}
                        inputConfig={inputConfig}
                        disabled
                      />
                      <FormItem
                        defaultValue=""
                        label={t('general.test_quantity')}
                        name="preEmailCount"
                        control={control}
                        inputConfig={inputConfig}
                        disabled
                      />
                    </Stack>
                    <Stack spacing={2}>
                      <FormItem
                        defaultValue=""
                        label={t('general.remaining_letters')}
                        name="manualEmailCount"
                        control={control}
                        inputConfig={inputConfig}
                        disabled
                      />
                      <FormItem
                        defaultValue=""
                        label={t('general.remaining_pre_letters')}
                        name="manualPreEmailCount"
                        control={control}
                        inputConfig={inputConfig}
                        disabled
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}

          <Typography mt={isReportOnlyRole ? 0 : 2} mb={1}>
            {t('general.basic_info')}
          </Typography>
          <Card>
            <CardContent>
              <Stack direction={'row'} spacing={4}>
                <Stack spacing={2}>
                  <FormItem
                    defaultValue=""
                    label={t('general.user_account')}
                    name="username"
                    control={control}
                    inputConfig={inputConfig}
                    disabled
                  />
                  <FormItem
                    defaultValue=""
                    label={t('general.account_type')}
                    name="type"
                    control={control}
                    inputConfig={inputConfig}
                    disabled
                  />
                  {!isReportOnlyRole && (
                    <FormItem
                      defaultValue=""
                      label={t('general.payment_tier')}
                      name="tier"
                      control={control}
                      inputConfig={inputConfig}
                      disabled
                    />
                  )}
                </Stack>

                <Stack spacing={2}>
                  {!isReportOnlyRole && (
                    <FormItem
                      defaultValue=""
                      label={t('general.account_manager')}
                      name="manager"
                      control={control}
                      inputConfig={inputConfig}
                      disabled
                    />
                  )}
                  <Stack direction={'row'}>
                    <FormItem
                      defaultValue="*******"
                      label={t('general.user_password')}
                      name="password"
                      control={control}
                      inputConfig={inputConfig}
                      disabled
                    />

                    <Button
                      variant="text"
                      sx={{
                        color: '#2D2D6C',
                        textDecoration: 'underline',
                      }}
                      onClick={() => {
                        setChangePassword(!changePassword)
                      }}
                    >
                      {t('general.change_password')}
                    </Button>
                  </Stack>
                  <FormItem
                    defaultValue=""
                    label={t('general.email')}
                    name="email"
                    control={control}
                    inputConfig={inputConfig}
                    disabled
                  />
                </Stack>
              </Stack>
              {!isReportOnlyRole && (
                <Stack mt={2}>
                  <FormItem
                    defaultValue=""
                    label={t('general.validity')}
                    name="validity"
                    control={control}
                    inputConfig={merge({}, inputConfig, {
                      inputBaseProps: { childrenSx: { width: 350 } },
                    })}
                    disabled
                  />
                </Stack>
              )}
            </CardContent>
          </Card>

          {!isReportOnlyRole && (
            <>
              <Typography mt={2} mb={1}>
                {t('general.verify_info')}
              </Typography>
              <Card>
                <CardContent>
                  <Stack direction={'row'}>
                    <Stack spacing={2}>
                      <FormItem
                        defaultValue=""
                        label={t('general.key')}
                        name="key"
                        control={control}
                        inputConfig={merge({}, inputConfig, {
                          inputBaseProps: { childrenSx: { width: 350 } },
                        })}
                        disabled
                      />
                      <FormItem
                        defaultValue=""
                        label={t('general.value')}
                        name="value"
                        control={control}
                        inputConfig={merge({}, inputConfig, {
                          inputBaseProps: { childrenSx: { width: 350 } },
                        })}
                        disabled
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>

        {!isReportOnlyRole && (
          <Stack sx={{ width: 300 }}>
            <Typography mb={1}>{t('general.service_desc')}</Typography>
            <Card sx={{ flex: 1 }}>
              <CardContent sx={{ pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography color={theme => theme.palette.secondary_80.main}>
                  {planName || '-'}
                </Typography>
                <Box pl={2} pt={2}>
                  <Typography color={theme => theme.palette.secondary_80.main} fontWeight={'bold'}>
                    {t('general.service_content')}
                  </Typography>
                  <PlanItem>
                    遠端執行
                    <PlanItemValue>寄送{emailCount}封</PlanItemValue>
                    釣魚郵件，提供企業電子郵件帳號
                    <PlanItemValue>{preEmailCount}次測試</PlanItemValue>
                    ，每次最高上限為
                    <PlanItemValue>{emailCount}封釣魚郵件</PlanItemValue>。
                  </PlanItem>

                  <PlanItem>
                    <PlanItemValue>{emailCount}封</PlanItemValue>
                    釣魚郵件裡包含
                    <PlanItemValue>3種</PlanItemValue>
                    社工程演練郵件樣板，含本文、附件以及可連結資訊。
                  </PlanItem>

                  {/* <PlanItem>
                    <PlanItemValue>{reportCount}份</PlanItemValue>演練報告。
                  </PlanItem>

                  <PlanItem>
                    <PlanItemValue>{courseCount}次</PlanItemValue>教育訓練。
                  </PlanItem> */}
                </Box>
                <Box flex={1} />
                {/* <Typography variant="caption">
                  以上服務內容，若有疑問請洽詢台灣大哥大 -資通訊暨個資安全管理處-資深工程師-鄭嘉智
                  0935-111222
                </Typography> */}
              </CardContent>
            </Card>
          </Stack>
        )}
      </Stack>

      {changePassword && (
        <ChangePasswordDialog
          open={changePassword}
          onClose={() => {
            setChangePassword(false)
          }}
        />
      )}
    </Container>
  )
}

const PlanItem = ({ children }) => (
  <Stack mt={2} direction={'row'} spacing={1}>
    <Box>
      <Circle color={'grey_40'} sx={{ height: 13, width: 13 }} />
    </Box>

    <Typography component="div" variant="subtitle2">
      {children}
    </Typography>
  </Stack>
)

const PlanItemValue = ({ children }) => (
  <Typography display={'inline'} color="primary" variant="subtitle2">
    {children}
  </Typography>
)

export default connect(mapStateToProps)(PersonalDetailsPage)
